const { groupForecastRows } = require('../tools/read/to-forecast-per-location')

const stripProductPrefix = (sku) =>
  typeof sku === 'string' && sku.startsWith('product:')
    ? sku.replace('product:', '')
    : sku

const addProductPrefix = (sku) =>
  typeof sku === 'string' && !sku.startsWith('product:')
    ? `product:${sku}`
    : sku

class ForecastDataAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async get (locationId, program, { sku } = {}) {
    if (!locationId) throw new Error('locationId is required')
    if (!program) throw new Error('program is required')

    const params = [locationId, program]
    let skuFilter = ''
    if (sku) {
      params.push(stripProductPrefix(sku))
      skuFilter = `AND f.sku = $${params.length}`
    }

    const { rows } = await this.pgConnection.query(`
      SELECT DISTINCT ON (f.sku)
        f.sku,
        f.daily_forecast,
        f.safety_stock,
        sp.supply_period_days,
        sp.lead_time_days,
        f.safety_stock AS min_level,
        f.safety_stock + f.daily_forecast * sp.lead_time_days AS reorder_point,
        f.safety_stock + f.daily_forecast * sp.supply_period_days AS max_level
      FROM avocado.data_forecast f
      JOIN avocado.data_location l ON f.location = l.uuid
      JOIN avocado.data_program p ON f.program = p.id
      JOIN LATERAL (
        SELECT supply_period_days, lead_time_days
        FROM avocado.data_supply_plan_params
        WHERE program = f.program
          AND sku = f.sku
          AND location_type = l.supply_chain_level
        ORDER BY date DESC
        LIMIT 1
      ) sp ON true
      WHERE l.fsid = $1
        AND p.fsid = $2
        ${skuFilter}
      ORDER BY f.sku, f.created_at DESC
    `, params)

    return rows.reduce((acc, row) => {
      const productId = addProductPrefix(row.sku)
      acc.products[productId] = {
        productId,
        dailyDemand: Number(row.daily_forecast),
        safetyStock: Number(row.safety_stock),
        supplyPeriodDays: Number(row.supply_period_days),
        leadTimeDays: Number(row.lead_time_days),
        minLevel: Number(row.min_level),
        reorderPoint: Number(row.reorder_point),
        maxLevel: Number(row.max_level)
      }
      return acc
    }, { locationId, products: {} })
  }

  // Returns aggregated forecast thresholds per SKU across all locations for the given program.
  // safety_stock, reorder_point and max_level are summed across all warehouse locations.
  // When consumptionLocationId is provided, dailyDemand is scoped to that location's latest
  // daily_forecast only, used to display AMC for a specific location on the national view.
  async getAggregated (program, consumptionLocationId = null) {
    if (!program) throw new Error('program is required')

    const { rows } = await this.pgConnection.query(`
      SELECT
        f.sku,
        SUM(f.daily_forecast)                                             AS daily_forecast,
        SUM(f.safety_stock)                                              AS safety_stock,
        SUM(f.safety_stock + f.daily_forecast * sp.lead_time_days)       AS reorder_point,
        SUM(f.safety_stock + f.daily_forecast * sp.supply_period_days)   AS max_level,
        loc_f.daily_forecast                                              AS location_daily_forecast
      FROM (
        SELECT DISTINCT ON (location, program, sku) *
        FROM avocado.data_forecast
        WHERE program = (SELECT id FROM avocado.data_program WHERE fsid = $1)
        ORDER BY location, program, sku, created_at DESC
      ) f
      JOIN avocado.data_location l ON f.location = l.uuid
      JOIN LATERAL (
        SELECT supply_period_days, lead_time_days
        FROM avocado.data_supply_plan_params
        WHERE program = f.program
          AND sku = f.sku
          AND location_type = l.supply_chain_level
        ORDER BY date DESC
        LIMIT 1
      ) sp ON true
      LEFT JOIN (
        SELECT DISTINCT ON (sku) sku, daily_forecast
        FROM avocado.data_forecast
        WHERE program = (SELECT id FROM avocado.data_program WHERE fsid = $1)
          AND location = (SELECT uuid FROM avocado.data_location WHERE fsid = $2)
        ORDER BY sku, created_at DESC
      ) loc_f ON f.sku = loc_f.sku
      GROUP BY f.sku, loc_f.daily_forecast
    `, [program, consumptionLocationId])

    return rows.reduce((acc, row) => {
      acc[addProductPrefix(row.sku)] = {
        dailyDemand: row.location_daily_forecast !== null ? Number(row.location_daily_forecast) : Number(row.daily_forecast),
        minLevel: Number(row.safety_stock),
        reorderPoint: Number(row.reorder_point),
        maxLevel: Number(row.max_level)
      }
      return acc
    }, {})
  }

  async list ({ locationIds, program, sku } = {}) {
    const conditions = []
    const params = []
    if (locationIds && locationIds.length > 0) {
      // Handle the case where a single locationId is passed as a string instead of an array
      const ids = Array.isArray(locationIds) ? locationIds : [locationIds]
      conditions.push(`location = ANY($${params.push(ids)}::text[])`)
    }
    if (program) {
      conditions.push(`program = $${params.push(program)}`)
    }
    if (sku) {
      conditions.push(`sku = $${params.push(stripProductPrefix(sku))}`)
    }
    const where = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : ''
    const { rows } = await this.pgConnection.query(`
      SELECT * FROM avocado.vw_supply_plan_values ${where}
    `, params)
    const prefixedRows = rows.map(row => ({ ...row, sku: addProductPrefix(row.sku) }))
    return groupForecastRows(prefixedRows)
  }
}

module.exports = ForecastDataAdapter
